import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { liteClient } from 'algoliasearch/lite';
import merge from 'lodash/merge';
import { FacetId } from '../types.js';
import { AlgoliaConfig } from './AlgoliaConfig.js';
import { UseQueryChange } from './hooks/use-query-change.js';
import { queryStringToQueryObject, queryObjectToSearchState, queryStringToSearchState, PRICE_FROM_PARAM, PRICE_TO_PARAM } from './utils/query-string.js';

function AlgoliaConfigProvider(props) {
    const { isEnabled , resultsState , clientQueryString , presetQueryString , NextRouter , ruleContexts , locale , facetingAfterDistinct , options: { indexes  } , isMultilingualEnabled ,  } = props;
    const { apiKey , appId , filters , indexName , pageSize =24 , indices  } = props.options;
    const searchClient = React.useMemo(()=>liteClient(appId, apiKey)
    , [
        appId,
        apiKey
    ]);
    const { facets: facets1 , sortIndices: sortIndices1 , presetQueryObject: presetQueryObject1 , initialSearchState: initialSearchState1  } = React.useMemo(()=>{
        const facets = configureFacets(props.options.facets, locale, presetQueryString);
        const sortIndices = props.options.sortIndices.filter(({ value  })=>value
        );
        const qsOptions = {
            facets,
            pageSize,
            sortIndices,
            presetQueryObject: {}
        };
        const presetQueryObject = queryStringToQueryObject(presetQueryString, qsOptions);
        const presetQueryState = queryObjectToSearchState(presetQueryObject, swcHelpers.objectSpread({}, qsOptions, {
            isCreatingPresetState: true,
            presetQueryObject
        }));
        const clientQueryState = queryStringToSearchState(clientQueryString, swcHelpers.objectSpread({}, qsOptions, {
            presetQueryObject
        }));
        const initialSearchState = merge(presetQueryState, clientQueryState);
        return {
            facets,
            sortIndices,
            presetQueryObject,
            initialSearchState
        };
    }, [
        props.options.facets,
        props.options.sortIndices,
        locale,
        presetQueryString,
        pageSize,
        clientQueryString, 
    ]);
    const [searchState, setSearchState] = React.useState({
        [indexName]: initialSearchState1
    });
    const onStateChange = React.useCallback(({ uiState , setUiState  })=>{
        setSearchState(uiState);
        setUiState(uiState);
    }, []);
    const value1 = React.useMemo(()=>({
            isEnabled,
            pageSize,
            filters,
            facets: facets1,
            facetingAfterDistinct,
            sortIndices: sortIndices1,
            presetQueryObject: presetQueryObject1,
            clientQueryString,
            ruleContexts,
            indices,
            locale,
            indexes,
            isMultilingualEnabled,
            algoliaInstantSearchProps: {
                onStateChange,
                indexName,
                resultsState,
                searchState,
                searchClient
            }
        })
    , [
        clientQueryString,
        isMultilingualEnabled,
        indexes,
        locale,
        isEnabled,
        pageSize,
        filters,
        facets1,
        facetingAfterDistinct,
        sortIndices1,
        presetQueryObject1,
        ruleContexts,
        indexName,
        resultsState,
        searchState,
        searchClient,
        indices,
        onStateChange, 
    ]);
    return /*#__PURE__*/ React.createElement(AlgoliaConfig.Provider, {
        value: value1
    }, isEnabled && NextRouter && /*#__PURE__*/ React.createElement(UseQueryChange, {
        NextRouter: NextRouter,
        searchState: searchState[indexName],
        clientQueryString: clientQueryString
    }), props.children);
}
function configureFacets(facets, locale, presetQueryString) {
    const presetKeyVal = Object.entries(queryStringToQueryObject(presetQueryString || '', {
        facets
    })).reduce((result, [key, value])=>{
        result[key.toLowerCase()] = value;
        return result;
    }, {});
    return facets.map((facet)=>{
        const rawValue = presetKeyVal[facet.id.toLowerCase()];
        const value = Array.isArray(rawValue) ? rawValue : rawValue ? [
            rawValue
        ] : [];
        let hidden = facet.hidden || !!value.length;
        if (facet.id === FacetId.price) {
            const hasPriceFrom = presetKeyVal.hasOwnProperty(PRICE_FROM_PARAM.toLowerCase());
            const hasPriceTo = presetKeyVal.hasOwnProperty(PRICE_TO_PARAM.toLowerCase());
            hidden = hidden || hasPriceFrom || hasPriceTo;
        }
        return swcHelpers.objectSpread({}, facet, {
            options: facet.options && lowercaseKeys(facet.options),
            value: value.length ? value : undefined,
            attribute: facet.isLocalized ? `${facet.attribute}_${locale}` : facet.attribute,
            hidden
        });
    });
}
function lowercaseKeys(obj) {
    return Object.keys(obj).reduce((result, key)=>{
        result[key.toLowerCase()] = obj[key];
        return result;
    }, {});
}

export { AlgoliaConfigProvider };
//# sourceMappingURL=AlgoliaConfigProvider.js.map
